/**
 * Custom hook for managing cloud sync state and actions.
 */

import { useState, useCallback } from 'react';
import type { SyncResult } from '@/hooks/useDocumentStorage';

interface UseCloudSyncOptions {
  syncToCloud: () => Promise<SyncResult>;
  onSyncSuccess: () => Promise<void>;
}

interface UseCloudSyncResult {
  showSyncModal: boolean;
  syncResult: SyncResult | null;
  handleSyncClick: () => Promise<void>;
  closeSyncModal: () => void;
}

export function useCloudSync({
  syncToCloud,
  onSyncSuccess,
}: UseCloudSyncOptions): UseCloudSyncResult {
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  const handleSyncClick = useCallback(async () => {
    setSyncResult(null);
    setShowSyncModal(true);
    const result = await syncToCloud();
    setSyncResult(result);
    if (result.success) {
      await onSyncSuccess();
    }
  }, [syncToCloud, onSyncSuccess]);

  const closeSyncModal = useCallback(() => {
    setShowSyncModal(false);
  }, []);

  return {
    showSyncModal,
    syncResult,
    handleSyncClick,
    closeSyncModal,
  };
}
